// src/app/components/molecules/ScheduleSelectorMolecule.tsx

import React from 'react';
import { Button } from 'primereact/button';
import ScheduleSummaryAtom from '../../atoms/ScheduleSummary/ScheduleSummaryAtom';
import { Schedule } from 'types/schedule';
import './ScheduleSelectorMolecule.scss';

export interface ScheduleSelectorMoleculeProps {
  /** The schedule object (same shape the atom expects) */
  schedule: { rrule: string } | Schedule | Partial<Schedule>;
  
  /** Optional dtstart override - if provided, will be used instead of dtstart from RRULE */
  dtstart?: string | Date;
  
  /** If supplied, shows an edit icon that invokes this handler */
  onEdit?: () => void;
  
  /** Extra CSS class on the wrapper */
  className?: string;
  
  /** Whether the schedule is editable (shows edit icon and styled container) */
  editable?: boolean;
  
  /** Optional label for the schedule selector */
  label?: string;
  
  /** Whether the component is disabled */
  disabled?: boolean;
}

const ScheduleSelectorMolecule: React.FC<ScheduleSelectorMoleculeProps> = ({
  schedule,
  dtstart,
  onEdit,
  className = '',
  editable = true,
  label,
  disabled = false,
}) => {
  console.log('[ScheduleSelectorMolecule] Rendered with:', { 
    schedule, 
    dtstart, 
    onEdit: !!onEdit, 
    editable, 
    disabled 
  });

  // Convert dtstart string to Date if needed
  let startDate: Date | undefined;
  if (dtstart) {
    if (typeof dtstart === 'string') {
      try {
        startDate = new Date(dtstart);
        // Validate the date
        if (isNaN(startDate.getTime())) {
          console.warn('[ScheduleSelectorMolecule] Invalid dtstart date:', dtstart);
          startDate = undefined;
        }
      } catch (error) {
        console.warn('[ScheduleSelectorMolecule] Error parsing dtstart:', dtstart, error);
        startDate = undefined;
      }
    } else if (dtstart instanceof Date) {
      startDate = dtstart;
    }
  }

  const handleEdit = () => {
    if (disabled) return;
    console.debug('[ScheduleSelectorMolecule] Edit icon clicked');
    onEdit?.();
  };

  // If not editable, just show the schedule summary without styling
  if (!editable) {
    return (
      <div className={`schedule-selector-molecule ${className}`}>
        {label && (
          <label className="schedule-selector-molecule__label">
            {label}
          </label>
        )}
        <ScheduleSummaryAtom 
          schedule={schedule} 
          dtstart={startDate}
        />
      </div>
    );
  }

  // Editable version with styled container
  return (
    <div className={`schedule-selector-molecule ${className}`}>
      {label && (
        <label className="schedule-selector-molecule__label">
          {label}
        </label>
      )}

      <span className="schedule-selector-molecule__input-container">
        <div className="schedule-selector-molecule__content">
          <ScheduleSummaryAtom 
            schedule={schedule} 
            dtstart={startDate}
          />
        </div>

        {onEdit && !disabled && (
          <Button
            type="button"
            icon="pi pi-pencil"
            className="schedule-selector-molecule__edit-btn"
            onClick={handleEdit}
            aria-label="Edit schedule"
            title="Edit schedule"
          />
        )}
      </span>
    </div>
  );
};

export default ScheduleSelectorMolecule;